import React, { ReactNode, useCallback } from "react";

import Tag from "antd/es/tag";
import cn from "classnames";
import { defineMessages, IntlShape } from "react-intl";

import Plan from "@mapmycustomers/shared/types/Plan";

import styles from "./Planitem.module.scss";
import { planNameToInfo } from "./planMessages";

const messages = defineMessages({
  current: {
    id: "customFieldsLimitAlertModal.plans.current",
    defaultMessage: "Current Plan",
  },
  customFields: {
    id: "customFieldsLimitAlertModal.plans.customFields",
    defaultMessage:
      "{limit, select, unlimited {Unlimited Custom Fields} other {{limit} {limit, plural, one {Custom Field} other {Custom Fields}}}}",
  },
  planFooter: {
    id: "customFieldsLimitAlertModal.plans.footer",
    defaultMessage: "+ many more ",
  },
  planSubtitle: {
    id: "customFieldsLimitAlertModal.plans.subtitle",
    defaultMessage: "Advanced capabilities for scaling organizations",
  },
  popular: {
    id: "customFieldsLimitAlertModal.plans.popular",
    defaultMessage: "Most Popular",
  },
  records: {
    id: "customFieldsLimitAlertModal.plans.records",
    defaultMessage:
      "{limit, select, unlimited {Unlimited Records Limit} other {{limit} {limit, plural, one {Record Limit} other {Records Limit}}}}",
  },
});

export interface PlanProps {
  currentPlan?: Plan;
  extra?: ReactNode;
  intl: IntlShape;
  onSelect?: (plan: Plan) => void;
  plan: Plan;
  selected?: boolean;
}

const PlanItem = ({ currentPlan, extra, intl, onSelect, plan, selected }: PlanProps) => {
  const info = planNameToInfo[plan.name as keyof typeof planNameToInfo];

  const handleClick = useCallback(() => {
    onSelect?.(plan);
  }, [onSelect, plan]);

  return (
    <div
      className={cn(styles.container, {
        [styles.popular]: info?.popular,
        [styles.selectable]: !!onSelect,
        [styles.selected]: selected,
      })}
      onClick={handleClick}
    >
      <div className={styles.header}>
        <div className={styles.title}>
          {plan.name}
          {currentPlan?.id === plan.id ? (
            <Tag color="processing">{intl.formatMessage(messages.current)}</Tag>
          ) : info?.popular ? (
            <Tag color="gold">{intl.formatMessage(messages.popular)}</Tag>
          ) : null}
        </div>
        {extra}
      </div>
      <div>
        <div className={styles.subtitle}>{intl.formatMessage(messages.planSubtitle)}</div>
        <ul className={styles.list}>
          <li key="customFields">
            {intl.formatMessage(messages.customFields, {
              limit: plan.customFieldsLimit === -1 ? "unlimited" : plan.customFieldsLimit,
            })}
          </li>
          <li key="records">
            {intl.formatMessage(messages.records, {
              limit: plan.recordsLimit === -1 ? "unlimited" : plan.recordsLimit,
            })}
          </li>
          {info?.extraPoints.map((point, index) => (
            <li key={index}>{intl.formatMessage(point)}</li>
          ))}
        </ul>
        <div className={styles.footer}>{intl.formatMessage(messages.planFooter)}</div>
      </div>
    </div>
  );
};

export default PlanItem;
